import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Box,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../services/api';

const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const DAY_DISPLAY_NAMES = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

const LOCATION_OPTIONS = [
  { value: 'npb', label: 'UTSA Main Campus (NPB 2.238 - ScooterLab)' },
  { value: 'sp1', label: 'UTSA School of Data Science (SP1-108 - ScooterLab)' }
];

const LOCATION_LABELS = {
  npb: 'Main Campus (NPB 2.238)',
  sp1: 'School of Data Science (SP1-108)'
};

// Generate time options (30-minute intervals from 6 AM to 11 PM)
const generateTimeOptions = () => {
  const times = [];
  for (let hour = 6; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      times.push(timeStr);
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

const MySchedule = () => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [formData, setFormData] = useState({
    day_of_week: '',
    location: 'npb',
    start_time: '09:00',
    end_time: '17:00'
  });

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/availability');
      const slots = response.data;
      
      // Group slots by day
      const grouped = {};
      DAYS_OF_WEEK.forEach(day => {
        grouped[day] = [];
      });
      
      slots.forEach(slot => {
        if (grouped[slot.day_of_week]) {
          grouped[slot.day_of_week].push(slot);
        }
      });
      
      // Sort slots by start_time within each day
      DAYS_OF_WEEK.forEach(day => {
        grouped[day].sort((a, b) => {
          return a.start_time.localeCompare(b.start_time);
        });
      });
      
      setAvailability(grouped);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (day = null, slot = null) => {
    if (slot) {
      // Editing existing slot
      setEditingSlot(slot);
      setFormData({
        day_of_week: slot.day_of_week,
        location: slot.location || 'npb',
        start_time: slot.start_time.substring(0, 5), // Convert HH:MM:SS to HH:MM
        end_time: slot.end_time.substring(0, 5)
      });
    } else {
      // Adding new slot
      setEditingSlot(null);
      setFormData({
        day_of_week: day || '',
        location: 'npb',
        start_time: '09:00',
        end_time: '17:00'
      });
    }
    setSelectedDay(day || slot?.day_of_week || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSlot(null);
    setFormData({
      day_of_week: '',
      location: 'npb',
      start_time: '09:00',
      end_time: '17:00'
    });
    setSelectedDay('');
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveSlot = async () => {
    try {
      setError('');
      setSuccess('');
      
      if (!formData.day_of_week || !formData.location || !formData.start_time || !formData.end_time) {
        setError('Please fill in all fields');
        return;
      }
      
      if (formData.start_time >= formData.end_time) {
        setError('End time must be after start time');
        return;
      }
      
      if (editingSlot) {
        // Update existing slot
        await api.put(`/availability/${editingSlot.id}`, formData);
        setSuccess('Availability updated successfully');
      } else {
        // Create new slot
        await api.post('/availability', formData);
        setSuccess('Availability added successfully');
      }
      
      handleCloseDialog();
      fetchAvailability();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save availability');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this time slot?')) {
      return;
    }
    
    try {
      setError('');
      await api.delete(`/availability/${slotId}`);
      setSuccess('Availability deleted successfully');
      fetchAvailability();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete availability');
    }
  };

  const formatTime = (timeStr) => {
    // Convert HH:MM:SS to HH:MM AM/PM
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScheduleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" gutterBottom>
            My Schedule
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage your weekly availability. Add time slots for each day when you're available.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {DAYS_OF_WEEK.map((day) => (
              <Grid item xs={12} sm={6} md={4} key={day}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" component="h2">
                        {DAY_DISPLAY_NAMES[day]}
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(day)}
                        aria-label={`Add availability for ${day}`}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    
                    {availability[day] && availability[day].length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {availability[day].map((slot) => (
                          <Paper
                            key={slot.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: 'action.hover'
                            }}
                          >
                            <Typography variant="body2">
                              {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                            </Typography>
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={LOCATION_LABELS[slot.location] || 'Main Campus (NPB 2.238)'}
                              sx={{ ml: 1 }}
                            />
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(null, slot)}
                                aria-label="Edit slot"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSlot(slot.id)}
                                aria-label="Delete slot"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No availability set
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSlot ? 'Edit Availability' : 'Add Availability'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Day of Week"
                name="day_of_week"
                value={formData.day_of_week}
                onChange={handleFormChange}
                fullWidth
                required
                disabled={!!editingSlot} // Disable if editing (can't change day)
              >
                {DAYS_OF_WEEK.map((day) => (
                  <MenuItem key={day} value={day}>
                    {DAY_DISPLAY_NAMES[day]}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Start Time"
                name="start_time"
                value={formData.start_time}
                onChange={handleFormChange}
                fullWidth
                required
              >
                {TIME_OPTIONS.map((time) => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time + ':00')}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleFormChange}
                fullWidth
                required
              >
                {LOCATION_OPTIONS.map((location) => (
                  <MenuItem key={location.value} value={location.value}>
                    {location.label}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="End Time"
                name="end_time"
                value={formData.end_time}
                onChange={handleFormChange}
                fullWidth
                required
              >
                {TIME_OPTIONS.map((time) => (
                  <MenuItem key={time} value={time}>
                    {formatTime(time + ':00')}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveSlot} variant="contained" color="primary">
              {editingSlot ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
};

export default MySchedule;

